<script setup lang="ts">
import type { Product } from '~~/shared/types/store'

const props = withDefaults(
  defineProps<{
    title: string
    products: Product[]
    isLoading?: boolean
    desktopColumns?: 3 | 4
    itemListId?: string
    itemListName?: string
  }>(),
  {
    isLoading: false,
    desktopColumns: 3,
    itemListId: undefined,
    itemListName: undefined,
  },
)
</script>

<template>
  <section
    v-if="props.isLoading || props.products.length"
    class="product-recommendations products-section container"
  >
    <h2 class="section-title">{{ props.title }}</h2>
    <div
      class="product-recommendations__grid"
      :class="[
        `product-recommendations__grid--desktop-${props.desktopColumns}`,
        {
          'product-recommendations__grid--loading': props.isLoading,
          'product-recommendations__grid--loaded': !props.isLoading && props.products.length > 0,
        },
      ]"
    >
      <template v-if="props.isLoading">
        <div
          v-for="index in props.desktopColumns"
          :key="index"
          class="product-recommendations__skeleton"
          aria-hidden="true"
        >
          <div class="product-recommendations__skeleton-image" />
          <div class="product-recommendations__skeleton-line product-recommendations__skeleton-line--short" />
          <div class="product-recommendations__skeleton-line" />
          <div class="product-recommendations__skeleton-line product-recommendations__skeleton-line--price" />
        </div>
      </template>
      <ProductCard
        v-for="(item, index) in props.products"
        v-else
        :key="item.id"
        :product="item"
        :item-list-id="props.itemListId ?? props.title"
        :item-list-name="props.itemListName ?? props.title"
        :index="index"
      />
    </div>
  </section>
</template>

<style scoped>
.product-recommendations {
  padding: 40px 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.section-title {
  margin: 0;
  font-size: 1.5rem;
  /* Tokeny marki — patrz `.section-title` w main.css. */
  font-weight: var(--section-title-weight, var(--font-weight-light));
  color: var(--section-title-color, var(--color-black));
  text-transform: var(--section-title-transform, none);
  letter-spacing: var(--section-title-tracking, normal);
  line-height: 1;
}

.product-recommendations__grid {
  display: grid;
  gap: 4px;
}

.product-recommendations__grid--loaded {
  animation: product-recommendations-fade-in 240ms ease-out;
}

.product-recommendations__grid--loading {
  align-items: start;
}

.product-recommendations__skeleton {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.product-recommendations__skeleton-image,
.product-recommendations__skeleton-line {
  background: var(--color-border-md);
  animation: product-recommendations-pulse 1.5s ease-in-out infinite;
}

.product-recommendations__skeleton-image {
  width: 100%;
  /* Ta sama proporcja co zdjęcie ProductCard — bez skoku po załadowaniu. */
  aspect-ratio: var(--product-card-img-ratio);
}

.product-recommendations__skeleton-line {
  height: 12px;
}

.product-recommendations__skeleton-line--short {
  width: 48%;
}

.product-recommendations__skeleton-line--price {
  width: 34%;
}

@keyframes product-recommendations-pulse {
  0% {
    opacity: 1;
  }

  50% {
    opacity: 0.4;
  }

  100% {
    opacity: 1;
  }
}

@keyframes product-recommendations-fade-in {
  from {
    opacity: 0;
    transform: translateY(8px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.product-recommendations__grid--desktop-4 {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.product-recommendations__grid--desktop-3 {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

@media (max-width: 1200px) {
  .product-recommendations__grid--desktop-4,
  .product-recommendations__grid--desktop-3 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

/* Proles: desktop od 1200 px jak listingi ([...slug].vue) — próg wyżej
   (max-width: 1200px) dawał 2 kafle dokładnie przy 1200 px. */
@media (min-width: 1200px) {
  [data-brand="proles"] .product-recommendations__grid--desktop-4 {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

@media (max-width: 960px) {
  .product-recommendations__grid--desktop-4,
  .product-recommendations__grid--desktop-3 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
